import React from 'react';
import { Solicitud } from '@/types/solicitud';
import { User } from '@/types/user';
import { Empresa } from '@/types/empresa';
import { Badge } from '@/components/ui/Badge';
import { Tabs } from '@/components/ui/Tabs';
import { Switch } from '@/components/ui/Switch';
import { usePlazosDisponibles } from '@/hooks/usePlazosDisponibles';
import { MontoInput } from '@/components/ui/MontoInput';
import { SimuladorSIAC } from './SimuladorSIAC';
import { useAuth } from '@/hooks/useAuth';
import { getCurrentUser } from '@/services/authService';

// Icono para tab Control de Fechas
const StepsIcon = () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5h10M9 12h10M9 19h10M5 6h.01M5 13h.01M5 20h.01" /></svg>;
const EyeIcon = () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>;
const DataIcon = () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>;

// Helper para formatear moneda
const formatCurrency = (amount: number | undefined | null) => {
  if (amount === undefined || amount === null) return '$0.00';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
};

// Helper para formatear fecha
const formatDate = (date: string | Date | undefined | null) => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(date);
  }
};

// Helper para formatear porcentaje
const formatPercent = (value: number | undefined | null) => {
  if (value === undefined || value === null) return '0%';
  return `${value}%`;
};

interface SolicitudControlFechasProps {
  localSolicitud: Solicitud;
  setLocalSolicitud: React.Dispatch<React.SetStateAction<Solicitud | null>>;
  setHasChanges: (v: boolean) => void;
  error: string | null;
  asesores: User[];
  allUsers?: User[]; // Todos los usuarios (para Asesor Origen)
  empresas: Empresa[]; // Empresas filtradas por el asesor actual
  loadingEmpresas?: boolean;
  onAsesorFinalChange?: (newAsesorId: string) => void;
  toggleDateField: (field: keyof Solicitud, enabled: boolean) => Promise<void>;
  updatingField: string | null;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  /** Callbacks opcionales para mostrar pestaña Simulador SIAC */
  onSimularSIAC?: () => Promise<void>;
  isSimulating?: boolean;
  /** Modo solo lectura - deshabilita todos los campos */
  readOnly?: boolean;
  /** Módulo actual para ocultar campos específicos */
  modulo?: string;
}

export function SolicitudControlFechas({
  localSolicitud,
  setLocalSolicitud,
  setHasChanges,
  error,
  asesores,
  allUsers,
  empresas,
  loadingEmpresas,
  onAsesorFinalChange,
  toggleDateField,
  activeTab,
  setActiveTab,
  onSimularSIAC,
  isSimulating,
  readOnly = false,
  modulo,
}: SolicitudControlFechasProps) {
  // Campos a ocultar en el módulo de revisión documental
  const isRevisionDocumental = modulo === 'revision-documental';
  const isAutorizacionDG = modulo === 'autorizacion-dg';
  // En módulo autorizacion-dg, deshabilitar todos los campos excepto switches de autorización/rechazo DG
  const fieldsDisabled = isAutorizacionDG;
  const { isFieldAllowed, userACL } = useAuth();
  const { plazos: plazosOptions } = usePlazosDisponibles();

  // Verificar si el usuario es promotor (por puesto) para habilitar sección Montos y Plazos
  const currentUser = getCurrentUser();
  const puestoLower = (currentUser?.puesto || '').toLowerCase();
  const esPromotor = puestoLower.includes('promotor');

  // En modo readOnly, todos los campos se muestran como solo lectura
  // Excepción: en módulo autorizacion-dg, permitir editar switches de autorización/rechazo DG y autorizado riesgos
  const canEdit = (field: string) => {
    // En módulo autorizacion-dg, permitir editar solo los campos de autorización/rechazo DG y autorizado riesgos
    if (isAutorizacionDG && (field === 'fechaAutorizacionDG' || field === 'fechaRechazoDG' || field === 'fechaPropuesta')) {
      return isFieldAllowed(field);
    }
    return !readOnly && isFieldAllowed(field);
  };
  const canEditInheritedID = canEdit('inheritedID');
  const canEditOwnerID = canEdit('ownerID');
  const canSeeRegion = isFieldAllowed('regionNombre'); // Ver siempre está permitido
  const canEditClienteEmpresa = canEdit('clienteEmpresa');
  const canSeeVoboRequerido = isFieldAllowed('voboRequerido');
  const canSeeClientePagadora = isFieldAllowed('clientePagadora');
  const canEditClienteNombre = canEdit('clienteNombre');
  const canEditClienteApellidoPaterno = canEdit('clienteApellidoPaterno');
  const canEditClienteApellidoMaterno = canEdit('clienteApellidoMaterno');
  const canEditClienteNuevoRenovacion = canEdit('clienteNuevoRenovacion');
  const canEditClientePuesto = canEdit('clientePuesto');
  const canEditClienteAntiguedad = canEdit('clienteAntiguedad');
  const canEditClienteClabe = canEdit('clienteClabe');
  const canEditClienteNombreJefe = canEdit('clienteNombreJefe');
  const canEditClienteCorreoJefe = canEdit('clienteCorreoJefe');
  const canEditClienteCorreo = canEdit('clienteCorreo');
  const canEditClienteSeEnteroPor = canEdit('clienteSeEnteroPor');
  const canEditProducto = canEdit('producto');
  const canEditPolizaSeguro = canEdit('polizaSeguro');
  // Campos de Montos y Plazos: promotores siempre pueden verlos
  const canEditMontoSolicitado = canEdit('montoSolicitado') || esPromotor;
  const canEditPlazoSolicitado = canEdit('plazoSolicitado') || esPromotor;
  const canEditSolicitudNumeroControl = canEdit('solicitudNumeroControl');
  const canEditFechaVencimientoLineaDeCredito = canEdit('fechaVencimientoLineaDeCredito') || esPromotor;
  const canEditMontoAutorizado = canEdit('montoAutorizado') || esPromotor;
  const canEditMontoComision = canEdit('montoComision') || esPromotor;
  const canEditPlazoAutorizado = canEdit('plazoAutorizado') || esPromotor;
  const canSeeAutorizacionDG = isFieldAllowed('autorizacionDG');
  const canEditUsuarioAutorizacionRiesgos = canEdit('usuarioAutorizacionRiesgos');
  const canEditMontoTransferencia = canEdit('montoTransferencia') || esPromotor;
  const canSeeDocumentos = isFieldAllowed('documentos');
  const canAddArchivos = !readOnly && isFieldAllowed('addArchivos');
  const canListaArchivos = isFieldAllowed('listaArchivos');
  const canEditFechaContacto = canEdit('fechaContacto');
  const canEditFechaEntregaARiesgos = canEdit('fechaEntregaARiesgos');
  const canEditFechaRevisionDocumentalConluida = canEdit('fechaRevisionDocumentalConluida');
  const canEditFechaAnalisisConluido = canEdit('fechaAnalisisConluido');
  const canEditFechaVoBo = canEdit('fechaVoBo');
  const canEditFechaPropuesta = canEdit('fechaPropuesta');
  const canEditFechaRechazoRiesgos = canEdit('fechaRechazoRiesgos');
  const canEditSolicitudCausaRechazoRiesgos = canEdit('solicitudCausaRechazoRiesgos');
  const canEditFechaAutorizacionDG = canEdit('fechaAutorizacionDG');
  const canEditFechaRechazoDG = canEdit('fechaRechazoDG');
  const canEditFechaContratoImpreso = canEdit('fechaContratoImpreso');
  const canEditFechaEntregaContratoFirmado = canEdit('fechaEntregaContratoFirmado');
  const canEditFechaFondeado = canEdit('fechaFondeado');
  const canEditFechaCancelacionCliente = canEdit('fechaCancelacionCliente');
  const canEditFechaCancelacionCartera = canEdit('fechaCancelacionCartera');
  const canSeeSolicitudEstatus = isFieldAllowed('solicitudEstatus');
  const canEditContratoDocumento = canEdit('contratoDocumento');
  const canSeeComentarios = isFieldAllowed('comentarios');

  // Helper para verificar si una propuesta tiene datos válidos
  const isPropuestaValida = (propuesta: any): boolean => {
    if (!propuesta) return false;
    // Verificar si tiene montoAutorizado mayor a 0
    const monto = propuesta.montoAutorizado;
    return monto !== undefined && monto !== null && monto > 0;
  };

  return (
    <>
        {/* Header Summary */}
        <div className="bg-gray-50 p-4 rounded-lg flex flex-wrap justify-between items-center gap-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{localSolicitud.clienteEmpresa}</h3>
            <p className="text-sm text-gray-500">Producto: {localSolicitud.producto}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-gray-500 uppercase">Monto Solicitado</p>
              <p className="font-bold text-gray-900">
                ${new Intl.NumberFormat('es-MX').format(localSolicitud.montoSolicitado || 0)}
              </p>
            </div>
            {canSeeSolicitudEstatus && (
            <Badge variant={localSolicitud.solicitudEstatus === 'AUTORIZADO' ? 'success' : 'info'}>
              {localSolicitud.solicitudEstatus}
            </Badge>
            )}
          </div>
        </div>

        {/* Tabs - Solo mostrar si no es readOnly o si hay simulador SIAC */}
        {(!readOnly || onSimularSIAC) && (
        <Tabs
          activeTab={activeTab}
          onChange={setActiveTab}
          items={[
            { id: 'switches', label: readOnly ? 'Información Completa' : 'Control de Fechas', icon: readOnly ? <DataIcon /> : <StepsIcon /> },
            ...(onSimularSIAC ? [{ id: 'simulador-siac', label: 'Simulador SIAC', icon: <EyeIcon /> }] : []),
          ]}
        />
        )}

        {/* Content */}
        <div className="pt-2">
          {error && (
            <div className="p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* TAB 1: FORMULARIO LEGACY + SWITCHES */}
          {activeTab === 'switches' && (
            <div className="space-y-4">
              {/* ========== FORMULARIO LEGACY (solo en modo edición) ========== */}
              {!readOnly && (<>
              <table className="w-full border-collapse text-sm table-fixed">
                <colgroup>
                  <col className="w-1/3" />
                  <col />
                  <col className="w-16" />
                </colgroup>
                <tbody>
                  {/* Sección 1: Asesor / Region */}
                  <tr className="bg-gray-100">
                    <td colSpan={3} className="px-3 py-2 font-bold text-gray-700 border border-gray-300">1. Asesor / Region</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Asesor Final</td>
                    <td className="px-3 py-2 border border-gray-200" colSpan={2}>
                      {canEditInheritedID ? (
                        <select
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          value={localSolicitud.inheritedID || ''}
                          onChange={(e) => onAsesorFinalChange ? onAsesorFinalChange(e.target.value) : setLocalSolicitud({ ...localSolicitud, inheritedID: e.target.value })}
                          disabled={fieldsDisabled}
                        >
                          <option value="">Seleccionar asesor...</option>
                          {asesores.map(asesor => (
                            <option key={asesor.ownerID} value={asesor.ownerID}>
                              {asesor.name || asesor.userName}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={asesores.find(a => a.ownerID === localSolicitud.inheritedID)?.name || asesores.find(a => a.ownerID === localSolicitud.inheritedID)?.userName || localSolicitud.inheritedID || ''} readOnly />
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Asesor Origen</td>
                    <td className="px-3 py-2 border border-gray-200">
                      {canEditOwnerID ? (
                        <select
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          value={localSolicitud.ownerID || ''}
                          onChange={(e) => { setLocalSolicitud({ ...localSolicitud, ownerID: e.target.value }); setHasChanges(true); }}
                          disabled={fieldsDisabled}
                        >
                          <option value="">Seleccionar asesor...</option>
                          {asesores.map(asesor => (
                            <option key={asesor.ownerID} value={asesor.ownerID}>
                              {asesor.name || asesor.userName}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={asesores.find(a => a.ownerID === localSolicitud.ownerID)?.name || asesores.find(a => a.ownerID === localSolicitud.ownerID)?.userName || localSolicitud.ownerID || ''} readOnly />
                      )}
                    </td>
                  </tr>
                  {canSeeRegion && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Region</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input
                        type="text"
                        className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50"
                        value={localSolicitud.regionNombre || ''}
                        readOnly
                      />
                    </td>
                  </tr>
                  )}
                  {canEditProducto && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Producto</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <select
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.producto || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, producto: e.target.value }); setHasChanges(true); }}
                        disabled={fieldsDisabled}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="Nómina">Nómina</option>
                        <option value="Adelanto de Nómina">Adelanto de Nómina</option>
                        <option value="Enganche Hipotecario">Enganche Hipotecario</option>
                      </select>
                    </td>
                  </tr>
                  )}

                  {/* Sección 2: Datos de la Empresa */}
                  {(canEditClienteEmpresa || canSeeVoboRequerido || canSeeClientePagadora) && (
                  <>
                  <tr className="bg-gray-100">
                    <td colSpan={3} className="px-3 py-2 font-bold text-gray-700 border border-gray-300">2. Datos de la Empresa</td>
                  </tr>
                  {canEditClienteEmpresa && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Empresa</td>
                    <td className="px-3 py-2 border border-gray-200">
                      {loadingEmpresas ? (
                        <div className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50 text-gray-500 flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Cargando empresas...
                        </div>
                      ) : empresas.length === 0 ? (
                        <div className="w-full px-2 py-1 border border-yellow-300 rounded bg-yellow-50 text-yellow-700 text-sm">
                          No hay empresas asignadas a este asesor
                        </div>
                      ) : (
                        <select
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          value={localSolicitud.clienteEmpresa || ''}
                          onChange={(e) => {
                            const nombre = e.target.value;
                            const emp = empresas.find(em => em.nombre === nombre);
                            setLocalSolicitud({
                              ...localSolicitud,
                              clienteEmpresa: nombre,
                              regionNombre: emp?.region || '',
                              voboRequerido: emp?.voboRequerido || '',
                              clientePagadora: emp?.empresaPagadora || '',
                              clienteTipoEmpresa: emp?.tipo || '',
                              clienteNivelRiesgoEmpresa: emp?.nivel || '',
                            });
                            setHasChanges(true);
                          }}
                          disabled={fieldsDisabled}
                        >
                          <option value="">Seleccionar empresa...</option>
                          {empresas.map(empresa => (
                            <option key={empresa.id || empresa.nombre} value={empresa.nombre}>
                              {empresa.nombre}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                  </tr>
                  )}
                  {canSeeVoboRequerido && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Se Requiere VoBo</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input
                        type="text"
                        className="w-full px-2 py-1 border border-gray-300 rounded bg-purple-100 text-purple-800 font-medium"
                        value={localSolicitud.voboRequerido || ''}
                        readOnly
                      />
                    </td>
                  </tr>
                  )}
                  {canSeeClientePagadora && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Pagadora</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input
                        type="text"
                        className="w-full px-2 py-1 border border-gray-300 rounded bg-purple-100 text-purple-800 font-medium"
                        value={localSolicitud.clientePagadora || ''}
                        readOnly
                      />
                    </td>
                  </tr>
                  )}
                  </>
                  )}

                  {/* Sección 3: Datos del Cliente */}
                  <tr className="bg-gray-100">
                    <td colSpan={3} className="px-3 py-2 font-bold text-gray-700 border border-gray-300">3. Datos del Cliente</td>
                  </tr>
                  {canEditClienteNombre && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Nombre</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input
                        type="text"
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.clienteNombre || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteNombre: e.target.value }); setHasChanges(true); }}
                        disabled={fieldsDisabled}
                      />
                    </td>
                  </tr>
                  )}
                  {canEditClienteApellidoPaterno && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Apellido Paterno</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input
                        type="text"
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.clienteApellidoPaterno || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteApellidoPaterno: e.target.value }); setHasChanges(true); }}
                        disabled={fieldsDisabled}
                      />
                    </td>
                  </tr>
                  )}
                  {canEditClienteApellidoMaterno && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Apellido Materno</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input
                        type="text"
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.clienteApellidoMaterno || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteApellidoMaterno: e.target.value }); setHasChanges(true); }}
                        disabled={fieldsDisabled}
                      />
                    </td>
                  </tr>
                  )}
                  {canEditClienteCorreo && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Correo del Cliente</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input
                        type="email"
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.clienteCorreo || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteCorreo: e.target.value }); setHasChanges(true); }}
                        placeholder="correo@ejemplo.com"
                        disabled={fieldsDisabled}
                      />
                    </td>
                  </tr>
                  )}
                  {canEditClienteNuevoRenovacion && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Nuevo/Renovación</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <select
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.clienteNuevoRenovacion || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteNuevoRenovacion: e.target.value }); setHasChanges(true); }}
                        disabled={fieldsDisabled}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="Nuevo">Nuevo</option>
                        <option value="Renovación">Renovación</option>
                      </select>
                    </td>
                  </tr>
                  )}
                  {canEditClientePuesto && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Puesto</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input
                        type="text"
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.clientePuesto || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clientePuesto: e.target.value }); setHasChanges(true); }}
                        disabled={fieldsDisabled}
                      />
                    </td>
                  </tr>
                  )}
                  {canEditClienteAntiguedad && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Antigüedad</td>
                    <td className="px-3 py-2 border border-gray-200">
                      {(localSolicitud.clienteEmpresa || '').toUpperCase() === 'LLANTERA ATLAS' ? (
                        <select
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          value={localSolicitud.clienteAntiguedad || ''}
                          onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteAntiguedad: e.target.value }); setHasChanges(true); }}
                          disabled={fieldsDisabled}
                        >
                          <option value="">Seleccionar...</option>
                          <option value="6 meses - 1 año">6 meses - 1 año</option>
                          <option value="1 año - 2 años">1 año - 2 años</option>
                          <option value="2 años - 4 años">2 años - 4 años</option>
                          <option value="Más de 4 años">Más de 4 años</option>
                          <option value="Tasa única excepción">Tasa única excepción</option>
                        </select>
                      ) : (
                        <select
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          value={localSolicitud.clienteAntiguedad || ''}
                          onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteAntiguedad: e.target.value }); setHasChanges(true); }}
                          disabled={fieldsDisabled}
                        >
                          <option value="">Seleccionar...</option>
                          <option value="0 - 6 meses">0 - 6 meses</option>
                          <option value="6 meses - 1 año">6 meses - 1 año</option>
                          <option value="1 año - 2 años">1 año - 2 años</option>
                          <option value="Más de 2 años">Más de 2 años</option>
                        </select>
                      )}
                    </td>
                  </tr>
                  )}
                  {canEditClienteClabe && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Cuenta Clabe</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.clienteClabe || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteClabe: e.target.value.replace(/\D/g, '') }); setHasChanges(true); }}
                        placeholder="Solo números"
                        disabled={fieldsDisabled}
                      />
                    </td>
                  </tr>
                  )}
                  {canEditClienteNombreJefe && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Nombre de Jefe Inmediato</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input
                        type="text"
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.clienteNombreJefe || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteNombreJefe: e.target.value }); setHasChanges(true); }}
                        disabled={fieldsDisabled}
                      />
                    </td>
                  </tr>
                  )}
                  {canEditClienteCorreoJefe && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Correo de Jefe Inmediato</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input
                        type="email"
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.clienteCorreoJefe || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteCorreoJefe: e.target.value }); setHasChanges(true); }}
                        disabled={fieldsDisabled}
                      />
                    </td>
                  </tr>
                  )}
                  {canEditClienteSeEnteroPor && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Se entero por</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <select
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.clienteSeEnteroPor || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, clienteSeEnteroPor: e.target.value }); setHasChanges(true); }}
                        disabled={fieldsDisabled}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="Correo del Asesor">Correo del Asesor</option>
                        <option value="Correo Masivo">Correo Masivo</option>
                        <option value="Flyer">Flyer</option>
                        <option value="Llamada del Asesor">Llamada del Asesor</option>
                        <option value="Presentacion del Asesor">Presentacion del Asesor</option>
                        <option value="Recomendacion">Recomendacion</option>
                      </select>
                    </td>
                  </tr>
                  )}
                  {canEditPolizaSeguro && localSolicitud.producto !== 'Adelanto de Nómina' && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Poliza de Seguro</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <select
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.polizaSeguro || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, polizaSeguro: e.target.value }); setHasChanges(true); }}
                        disabled={fieldsDisabled}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="Ninguna">Ninguna</option>
                        <option value="25000">$25,000</option>
                        <option value="50000">$50,000</option>
                        <option value="75000">$75,000</option>
                        <option value="100000">$100,000</option>
                      </select>
                    </td>
                  </tr>
                  )}

                  {/* Sección 4: Montos y Plazos */}
                  <tr className="bg-gray-100">
                    <td colSpan={3} className="px-3 py-2 font-bold text-gray-700 border border-gray-300">4. Montos y Plazos</td>
                  </tr>
                  {canEditMontoSolicitado && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Solicitado ($)</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <MontoInput
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.montoSolicitado}
                        onChange={(n) => { setLocalSolicitud({ ...localSolicitud, montoSolicitado: n }); setHasChanges(true); }}
                        placeholder="Ingrese el monto"
                        disabled={fieldsDisabled}
                      />
                    </td>
                  </tr>
                  )}
                  {canEditMontoAutorizado && !isRevisionDocumental && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Autorizado ($)</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <MontoInput
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.montoAutorizado}
                        onChange={(n) => { setLocalSolicitud({ ...localSolicitud, montoAutorizado: n }); setHasChanges(true); }}
                        placeholder="Ingrese el monto"
                        disabled={fieldsDisabled}
                      />
                    </td>
                  </tr>
                  )}
                  {canEditMontoTransferencia && !isRevisionDocumental && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Transferencia ($)</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <MontoInput
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.montoTransferencia}
                        onChange={(n) => { setLocalSolicitud({ ...localSolicitud, montoTransferencia: n }); setHasChanges(true); }}
                        placeholder="Ingrese el monto"
                        disabled={fieldsDisabled}
                      />
                    </td>
                  </tr>
                  )}
                  {canEditMontoComision && !isRevisionDocumental && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Comision Autorizada($)</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <MontoInput
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.montoComision}
                        onChange={(n) => { setLocalSolicitud({ ...localSolicitud, montoComision: n }); setHasChanges(true); }}
                        placeholder="Ingrese el monto"
                        disabled={fieldsDisabled}
                      />
                    </td>
                  </tr>
                  )}
                  {canEditPlazoSolicitado && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Plazo Solicitado en meses</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <select
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.plazoSolicitado || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, plazoSolicitado: e.target.value }); setHasChanges(true); }}
                        disabled={fieldsDisabled}
                      >
                        <option value="">Seleccionar...</option>
                        {plazosOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  )}
                  {canEditPlazoAutorizado && !isRevisionDocumental && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Plazo Autorizado en meses</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <select
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={localSolicitud.plazoAutorizado || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, plazoAutorizado: e.target.value }); setHasChanges(true); }}
                        disabled={fieldsDisabled}
                      >
                        <option value="">Seleccionar...</option>
                        {plazosOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  )}
                  {canEditFechaVencimientoLineaDeCredito && !isRevisionDocumental && (localSolicitud.producto || '').toLowerCase().includes('adelanto') && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Vencimiento de Línea de Cto</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input
                        type="date"
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={String(localSolicitud.fechaVencimientoLineaDeCredito || '').split('T')[0].split(' ')[0]}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, fechaVencimientoLineaDeCredito: e.target.value }); setHasChanges(true); }}
                        disabled={fieldsDisabled}
                      />
                    </td>
                  </tr>
                  )}

                  {/* ========== CONTINUACIÓN: CONTROL, FECHAS, SWITCHES ========== */}
                  <tr className="bg-gray-100">
                    <td colSpan={3} className="px-3 py-2 font-bold text-gray-700 border border-gray-300">5. Control de Fechas</td>
                  </tr>
                  {/* Numero de Control */}
                  {canEditSolicitudNumeroControl && !isRevisionDocumental && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200 w-1/3">Numero de Control</td>
                    <td className="px-3 py-2 border border-gray-200" colSpan={2}>
                      <input
                        type="text"
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        value={localSolicitud.solicitudNumeroControl || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, solicitudNumeroControl: e.target.value }); setHasChanges(true); }}
                        disabled={fieldsDisabled}
                      />
                    </td>
                  </tr>
                  )}

                  {/* Solicitud (Contacto) */}
                  {canEditFechaContacto && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Solicitud</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.fechaContacto || ''} readOnly />
                    </td>
                    <td className="px-1 py-2 border border-gray-200 w-16 text-center">
                      <Switch checked={!!localSolicitud.fechaContacto} onChange={(checked) => toggleDateField('fechaContacto' as keyof Solicitud, checked)} color="blue" disabled={fieldsDisabled} />
                    </td>
                  </tr>
                  )}

                  {/* Entrega a Riesgos */}
                  {canEditFechaEntregaARiesgos && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Entrega a Riesgos</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.fechaEntregaARiesgos || ''} readOnly />
                    </td>
                    <td className="px-1 py-2 border border-gray-200 w-16 text-center">
                      <Switch checked={!!localSolicitud.fechaEntregaARiesgos} onChange={(checked) => toggleDateField('fechaEntregaARiesgos' as keyof Solicitud, checked)} color="blue" disabled={!localSolicitud.fechaContacto || fieldsDisabled} />
                    </td>
                  </tr>
                  )}

                  {/* Revisión Documental Concluida */}
                  {canEditFechaRevisionDocumentalConluida && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Revisión Documental Concluida</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.fechaRevisionDocumentalConluida || ''} readOnly />
                    </td>
                    <td className="px-1 py-2 border border-gray-200 w-16 text-center">
                      <Switch checked={!!localSolicitud.fechaRevisionDocumentalConluida} onChange={(checked) => toggleDateField('fechaRevisionDocumentalConluida' as keyof Solicitud, checked)} color="blue" disabled={!localSolicitud.fechaEntregaARiesgos || fieldsDisabled} />
                    </td>
                  </tr>
                  )}

                  {/* Análisis Concluido */}
                  {canEditFechaAnalisisConluido && !isRevisionDocumental && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Análisis Concluido</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.fechaAnalisisConluido || ''} readOnly />
                    </td>
                    <td className="px-1 py-2 border border-gray-200 w-16 text-center">
                      <Switch checked={!!localSolicitud.fechaAnalisisConluido} onChange={(checked) => toggleDateField('fechaAnalisisConluido' as keyof Solicitud, checked)} color="blue" disabled={!localSolicitud.fechaRevisionDocumentalConluida || fieldsDisabled} />
                    </td>
                  </tr>
                  )}

                  {/* Recibe VoBo */}
                  {canEditFechaVoBo && !isRevisionDocumental && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Recibe VoBo</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.fechaVoBo || ''} readOnly />
                    </td>
                    <td className="px-1 py-2 border border-gray-200 w-16 text-center">
                      <Switch checked={!!localSolicitud.fechaVoBo} onChange={(checked) => toggleDateField('fechaVoBo' as keyof Solicitud, checked)} color="blue" disabled={!localSolicitud.fechaAnalisisConluido || fieldsDisabled} />
                    </td>
                  </tr>
                  )}

                  {/* Autorizado Riesgos */}
                  {canEditFechaPropuesta && !isRevisionDocumental && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Autorizado Riesgos</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.fechaPropuesta || ''} readOnly />
                    </td>
                    <td className="px-1 py-2 border border-gray-200 w-16 text-center">
                      <Switch checked={!!localSolicitud.fechaPropuesta} onChange={(checked) => toggleDateField('fechaPropuesta' as keyof Solicitud, checked)} color="blue" disabled={!localSolicitud.fechaVoBo || (fieldsDisabled && !isAutorizacionDG)} />
                    </td>
                  </tr>
                  )}

                  {/* Riesgos rechaza por (select) */}
                  {canEditSolicitudCausaRechazoRiesgos && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Riesgos rechaza por</td>
                    <td className="px-3 py-2 border border-gray-200" colSpan={2}>
                      <select
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        value={localSolicitud.motivoNoCierre || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, motivoNoCierre: e.target.value }); setHasChanges(true); }}
                        disabled={fieldsDisabled}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="Clave 100">Clave 100</option>
                        <option value="Clave 101">Clave 101</option>
                        <option value="Falta Solicitud">Falta Solicitud</option>
                        <option value="Falta Identificación">Falta Identificación</option>
                        <option value="Falta Comprobante de Domicilio">Falta Comprobante de Domicilio</option>
                        <option value="Es PEP">Es PEP</option>
                        <option value="Es Persona Bloqueada">Es Persona Bloqueada</option>
                        <option value="Falta Aval">Falta Aval</option>
                        <option value="Buro del Aval">Buro del Aval</option>
                        <option value="Falta de Liquidez">Falta de Liquidez</option>
                        <option value="Mínimo Solicitado">Mínimo Solicitado</option>
                        <option value="Nivel de Riesgo">Nivel de Riesgo</option>
                        <option value="Firma Apocrifa">Firma Apocrifa</option>
                        <option value="Firma Digital">Firma Digital</option>
                        <option value="Contrato No Pasa">Contrato No Pasa</option>
                        <option value="Monto excede políticas">Monto excede políticas</option>
                        <option value="Por Políticas">Por Políticas</option>
                      </select>
                    </td>
                  </tr>
                  )}

                  {/* Rechazado Riesgos */}
                  {canEditFechaRechazoRiesgos && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Rechazado Riesgos</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.fechaRechazoRiesgos || ''} readOnly />
                    </td>
                    <td className="px-1 py-2 border border-gray-200 w-16 text-center">
                      <Switch checked={!!localSolicitud.fechaRechazoRiesgos} onChange={(checked) => toggleDateField('fechaRechazoRiesgos' as keyof Solicitud, checked)} color="blue" disabled={fieldsDisabled} />
                    </td>
                  </tr>
                  )}

                  {/* Autorizacion de D.G. (solo lectura - no editable) */}
                  {canSeeAutorizacionDG && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Autorizacion de D.G.</td>
                    <td className="px-3 py-2 border border-gray-200" colSpan={2}>
                      <div className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                        {localSolicitud.autorizacionDG || 'Normal'}
                      </div>
                    </td>
                  </tr>
                  )}

                  {/* Autorizado DG */}
                  {canEditFechaAutorizacionDG && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Autorizado DG</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.fechaAutorizacionDG || ''} readOnly />
                    </td>
                    <td className="px-1 py-2 border border-gray-200 w-16 text-center">
                      <Switch checked={!!localSolicitud.fechaAutorizacionDG} onChange={(checked) => toggleDateField('fechaAutorizacionDG' as keyof Solicitud, checked)} color="blue" disabled={!localSolicitud.fechaPropuesta || localSolicitud.autorizacionDG !== 'Requerida'} />
                    </td>
                  </tr>
                  )}

                  {/* Rechazado DG */}
                  {canEditFechaRechazoDG && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Rechazado DG</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.fechaRechazoDG || ''} readOnly />
                    </td>
                    <td className="px-1 py-2 border border-gray-200 w-16 text-center">
                      <Switch checked={!!localSolicitud.fechaRechazoDG} onChange={(checked) => toggleDateField('fechaRechazoDG' as keyof Solicitud, checked)} color="blue" />
                    </td>
                  </tr>
                  )}

                  {/* Contrato Impreso */}
                  {canEditFechaContratoImpreso && !isRevisionDocumental && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Contrato Impreso</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.fechaContratoImpreso || ''} readOnly />
                    </td>
                    <td className="px-1 py-2 border border-gray-200 w-16 text-center">
                      <Switch checked={!!localSolicitud.fechaContratoImpreso} onChange={(checked) => toggleDateField('fechaContratoImpreso' as keyof Solicitud, checked)} color="blue" disabled={!(localSolicitud.fechaPropuesta && (localSolicitud.autorizacionDG !== 'Requerida' || localSolicitud.fechaAutorizacionDG)) || fieldsDisabled} />
                    </td>
                  </tr>
                  )}

                  {/* Contrato Firmado */}
                  {canEditFechaEntregaContratoFirmado && !isRevisionDocumental && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Contrato Firmado</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.fechaEntregaContratoFirmado || ''} readOnly />
                    </td>
                    <td className="px-1 py-2 border border-gray-200 w-16 text-center">
                      <Switch checked={!!localSolicitud.fechaEntregaContratoFirmado} onChange={(checked) => toggleDateField('fechaEntregaContratoFirmado' as keyof Solicitud, checked)} color="blue" disabled={!localSolicitud.fechaContratoImpreso || fieldsDisabled} />
                    </td>
                  </tr>
                  )}

                  {/* Cumple el Contrato? (select) */}
                  {canEditContratoDocumento && !isRevisionDocumental && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Cumple el Contrato?</td>
                    <td className="px-3 py-2 border border-gray-200" colSpan={2}>
                      <select
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        value={localSolicitud.contratoDocumento || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, contratoDocumento: e.target.value }); setHasChanges(true); }}
                        disabled={fieldsDisabled}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="Ok">Ok</option>
                        <option value="No Cumple">No Cumple</option>
                      </select>
                    </td>
                  </tr>
                  )}

                  {/* Fondeado */}
                  {canEditFechaFondeado && !isRevisionDocumental && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Fondeado</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.fechaFondeado || ''} readOnly />
                    </td>
                    <td className="px-1 py-2 border border-gray-200 w-16 text-center">
                      <span title={!!localSolicitud.fechaFondeado && userACL !== 'analistaRiesgos' ? 'Solo Analista de Riesgos puede regresar a Por Fondear' : undefined}>
                        <Switch
                          checked={!!localSolicitud.fechaFondeado}
                          onChange={(checked) => toggleDateField('fechaFondeado' as keyof Solicitud, checked)}
                          color="blue"
                          disabled={!localSolicitud.fechaEntregaContratoFirmado || (!!localSolicitud.fechaFondeado && userACL !== 'analistaRiesgos') || fieldsDisabled}
                        />
                      </span>
                    </td>
                  </tr>
                  )}

                  {/* Cancelado por Cliente */}
                  {canEditFechaCancelacionCliente && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Cancelado por Cliente</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.fechaCancelacionCliente || ''} readOnly />
                    </td>
                    <td className="px-1 py-2 border border-gray-200 w-16 text-center">
                      <Switch checked={!!localSolicitud.fechaCancelacionCliente} onChange={(checked) => toggleDateField('fechaCancelacionCliente' as keyof Solicitud, checked)} color="blue" disabled={fieldsDisabled} />
                    </td>
                  </tr>
                  )}

                  {/* Cancelado por Cartera */}
                  {canEditFechaCancelacionCartera && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Cancelado por Cartera</td>
                    <td className="px-3 py-2 border border-gray-200">
                      <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.fechaCancelacionCartera || ''} readOnly />
                    </td>
                    <td className="px-1 py-2 border border-gray-200 w-16 text-center">
                      <Switch checked={!!localSolicitud.fechaCancelacionCartera} onChange={(checked) => toggleDateField('fechaCancelacionCartera' as keyof Solicitud, checked)} color="blue" disabled={fieldsDisabled} />
                    </td>
                  </tr>
                  )}

                  {/* Estatus (solo lectura - no editable) */}
                  {canSeeSolicitudEstatus && !isRevisionDocumental && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Estatus</td>
                    <td className="px-3 py-2 border border-gray-200" colSpan={2}>
                      <div className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                        {localSolicitud.solicitudEstatus || 'CONTACTO'}
                      </div>
                    </td>
                  </tr>
                  )}

                  {/* Autorizado en Riesgos por - Siempre solo lectura, se asigna automáticamente */}
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Autorizado en Riesgos por</td>
                    <td className="px-3 py-2 border border-gray-200" colSpan={2}>
                      <input
                        type="text"
                        className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50"
                        value={localSolicitud.usuarioAutorizacionRiesgos || ''}
                        readOnly
                      />
                    </td>
                  </tr>

                  {/* Link Documentos */}
                  {canSeeDocumentos && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Link Documentos</td>
                    <td className="px-3 py-2 border border-gray-200" colSpan={2}>
                      {canAddArchivos ? (
                        <input
                          type="text"
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                          value={localSolicitud.documentos || ''}
                          onChange={(e) => { setLocalSolicitud({ ...localSolicitud, documentos: e.target.value }); setHasChanges(true); }}
                          placeholder="https://drive.google.com/..."
                          disabled={fieldsDisabled}
                        />
                      ) : (
                        <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50" value={localSolicitud.documentos || ''} readOnly />
                      )}
                    </td>
                  </tr>
                  )}
                  {canListaArchivos && localSolicitud.documentos && (
                    <tr>
                      <td className="px-3 py-2 border border-gray-200"></td>
                      <td className="px-3 py-2 border border-gray-200" colSpan={2}>
                        <button
                          type="button"
                          className="px-4 py-1 border border-gray-400 rounded bg-gray-100 hover:bg-gray-200 text-sm"
                          onClick={() => window.open(localSolicitud.documentos, '_blank')}
                        >
                          Ver Documentos
                        </button>
                      </td>
                    </tr>
                  )}

                  {/* Comentarios */}
                  {canSeeComentarios && (
                  <>
                  <tr>
                    <td className="px-3 py-2 text-left font-medium text-gray-600 border border-gray-200" colSpan={3}>Comentarios</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 border border-gray-200" colSpan={3}>
                      <textarea
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        rows={4}
                        value={localSolicitud.comentarios || ''}
                        onChange={(e) => { setLocalSolicitud({ ...localSolicitud, comentarios: e.target.value }); setHasChanges(true); }}
                        placeholder="Escribe comentarios aquí..."
                        disabled={fieldsDisabled}
                      />
                    </td>
                  </tr>
                  </>
                  )}
                </tbody>
              </table>
              </>)}

              {/* DATOS COMPLETOS - Se muestra SOLO en modo readOnly (mismo formato de tabla) */}
              {readOnly && (
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {/* Sección: Asesor / Región */}
                  <tr className="bg-gray-100">
                    <td colSpan={2} className="px-3 py-2 font-bold text-gray-700 border border-gray-300">1. Asesor / Región</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Asesor Final</td>
                    <td className="px-3 py-2 border border-gray-200" colSpan={2}>{localSolicitud.asesorNombre || localSolicitud.inheritedID || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Asesor Origen</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.ownerID || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Región</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.regionNombre || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Producto</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.producto || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Número de Control</td>
                    <td className="px-3 py-2 border border-gray-200 font-mono">{localSolicitud.solicitudNumeroControl || '—'}</td>
                  </tr>

                  {/* Sección: Datos de la Empresa */}
                  <tr className="bg-gray-100">
                    <td colSpan={3} className="px-3 py-2 font-bold text-gray-700 border border-gray-300">2. Datos de la Empresa</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Empresa</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.clienteEmpresa || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">VoBo Requerido</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.voboRequerido || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Pagadora</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.clientePagadora || '—'}</td>
                  </tr>

                  {/* Sección: Datos del Cliente */}
                  <tr className="bg-gray-100">
                    <td colSpan={3} className="px-3 py-2 font-bold text-gray-700 border border-gray-300">3. Datos del Cliente</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Nombre Completo</td>
                    <td className="px-3 py-2 border border-gray-200 font-semibold">{[localSolicitud.clienteNombre, localSolicitud.clienteApellidoPaterno, localSolicitud.clienteApellidoMaterno].filter(Boolean).join(' ') || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Nuevo / Renovación</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.clienteNuevoRenovacion || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Puesto</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.clientePuesto || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Antigüedad</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.clienteAntiguedad || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Cuenta CLABE</td>
                    <td className="px-3 py-2 border border-gray-200 font-mono">{localSolicitud.clienteClabe || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Nombre Jefe Inmediato</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.clienteNombreJefe || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Correo Jefe</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.clienteCorreoJefe || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Se enteró por</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.clienteSeEnteroPor || '—'}</td>
                  </tr>

                  {/* Sección: Datos Financieros */}
                  <tr className="bg-blue-100">
                    <td colSpan={2} className="px-3 py-2 font-bold text-blue-800 border border-gray-300">4. Datos Financieros</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200 w-1/3">Monto Solicitado</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.montoSolicitado)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Autorizado</td>
                    <td className="px-3 py-2 border border-gray-200 text-green-700 font-semibold">{formatCurrency(localSolicitud.montoAutorizado)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Transferencia</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.montoTransferencia)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Ministración</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.montoMinistracion)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Comisión</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.montoComision)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">IVA Comisión</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.montoIvaComision)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Seguro</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.montoSeguro)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">IVA Seguro</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.montoIvaSeguro)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Seguro Financiado</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.solicitudSeguroFinanciado)}</td>
                  </tr>
                  {(localSolicitud.producto || '').toLowerCase().includes('adelanto') && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Línea de Crédito</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.lineaDeCredito)}</td>
                  </tr>
                  )}
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Propuesto</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.montoPropuesto)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Capacidad de Pago</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.montoCapacidadDePago)}</td>
                  </tr>

                  {/* Sección: Tasas y Plazos */}
                  <tr className="bg-green-100">
                    <td colSpan={2} className="px-3 py-2 font-bold text-green-800 border border-gray-300">5. Tasas y Plazos</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Plazo Solicitado</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.plazoSolicitado || '—'} meses</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Plazo Autorizado</td>
                    <td className="px-3 py-2 border border-gray-200 text-green-700 font-semibold">{localSolicitud.plazoAutorizado || '—'} meses</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Tasa Autorizada</td>
                    <td className="px-3 py-2 border border-gray-200">{formatPercent(localSolicitud.tasaAutorizada)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Tasa Política</td>
                    <td className="px-3 py-2 border border-gray-200">{formatPercent(localSolicitud.tasaPolitica)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Tasa de Comisión</td>
                    <td className="px-3 py-2 border border-gray-200">{formatPercent(localSolicitud.tasaDeComision)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Frecuencia de Pago</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.frecuenciaDePago || '—'}</td>
                  </tr>
                  {localSolicitud.producto !== 'Adelanto de Nómina' && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Póliza Seguro</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.polizaSeguro || '—'}</td>
                  </tr>
                  )}

                  {/* Sección: Datos de Pagos */}
                  <tr className="bg-purple-100">
                    <td colSpan={2} className="px-3 py-2 font-bold text-purple-800 border border-gray-300">6. Datos de Pagos</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Número de Pagos</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.numeroPagos || 0}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Pago Estimado Periodo</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.pagoEstimadoPeriodo)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Pago Total Periodo</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.pagoTotalPeriodo)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Parcialidad Simulador</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.pagoParcialidadSimulador)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Total Intereses Simulador</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.totalInteresesSimulador)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Pago Total Simulador</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.pagoTotalSimulador)}</td>
                  </tr>

                  {/* Sección: Fechas del Ciclo de Vida */}
                  <tr className="bg-orange-100">
                    <td colSpan={2} className="px-3 py-2 font-bold text-orange-800 border border-gray-300">7. Fechas del Ciclo de Vida</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Fecha Contacto</td>
                    <td className="px-3 py-2 border border-gray-200">{formatDate(localSolicitud.fechaContacto)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Fecha Entrega a Riesgos</td>
                    <td className="px-3 py-2 border border-gray-200">{formatDate(localSolicitud.fechaEntregaARiesgos)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Fecha Análisis Concluido</td>
                    <td className="px-3 py-2 border border-gray-200">{formatDate(localSolicitud.fechaAnalisisConluido)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Fecha VoBo</td>
                    <td className="px-3 py-2 border border-gray-200">{formatDate(localSolicitud.fechaVoBo)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Fecha Propuesta (Auth Riesgos)</td>
                    <td className="px-3 py-2 border border-gray-200">{formatDate(localSolicitud.fechaPropuesta)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Fecha Autorización DG</td>
                    <td className="px-3 py-2 border border-gray-200">{formatDate(localSolicitud.fechaAutorizacionDG)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Fecha Contrato Impreso</td>
                    <td className="px-3 py-2 border border-gray-200">{formatDate(localSolicitud.fechaContratoImpreso)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Fecha Contrato Firmado</td>
                    <td className="px-3 py-2 border border-gray-200">{formatDate(localSolicitud.fechaEntregaContratoFirmado)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Fecha Fondeado</td>
                    <td className="px-3 py-2 border border-gray-200 text-green-700 font-semibold">{formatDate(localSolicitud.fechaFondeado)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Fecha Emisión Contrato</td>
                    <td className="px-3 py-2 border border-gray-200">{formatDate(localSolicitud.fechaEmisionContrato)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Fecha Primer Cobro</td>
                    <td className="px-3 py-2 border border-gray-200">{formatDate(localSolicitud.fechaPrimerCobro)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Fecha Último Pago</td>
                    <td className="px-3 py-2 border border-gray-200">{formatDate(localSolicitud.fechaUltimoPago)}</td>
                  </tr>
                  {(localSolicitud.producto || '').toLowerCase().includes('adelanto') && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Vencimiento Línea Crédito</td>
                    <td className="px-3 py-2 border border-gray-200">{formatDate(localSolicitud.fechaVencimientoLineaDeCredito)}</td>
                  </tr>
                  )}
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Fecha Rechazo Riesgos</td>
                    <td className="px-3 py-2 border border-gray-200 text-red-600">{formatDate(localSolicitud.fechaRechazoRiesgos)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Fecha Rechazo DG</td>
                    <td className="px-3 py-2 border border-gray-200 text-red-600">{formatDate(localSolicitud.fechaRechazoDG)}</td>
                  </tr>

                  {/* Sección: Datos del Cliente Adicionales */}
                  <tr className="bg-teal-100">
                    <td colSpan={2} className="px-3 py-2 font-bold text-teal-800 border border-gray-300">8. Datos del Cliente (Adicionales)</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Salario</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.clienteSalario)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Fecha Ingreso</td>
                    <td className="px-3 py-2 border border-gray-200">{formatDate(localSolicitud.clienteFechaIngreso)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Ded. Periodo</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.clienteDeduccionesPeriodo)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Ded. Mensuales</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.clienteDeduccionesMensuales)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Ded. Quincenales</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.clienteDeduccionesQuincenales)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Ded. Semanales</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.clienteDeduccionesSemanales)}</td>
                  </tr>

                  {/* Sección: Análisis de Riesgos */}
                  <tr className="bg-yellow-100">
                    <td colSpan={2} className="px-3 py-2 font-bold text-yellow-800 border border-gray-300">9. Análisis de Riesgos</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Estatus Análisis</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.estatusAnalisis || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Analista</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.analistaNombre || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Usuario Auth Riesgos</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.usuarioAutorizacionRiesgos || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Tipo Buró Crédito</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.tipoBuroCredito || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Tipo Lista Negra</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.tipoListaNegra || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">MOP</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.solicitudMop || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Saldo 1-89 días</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.saldo1a89)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Saldo &gt;90 días</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.saldoMayor90)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Saldo &gt;120 días</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.saldoMayor120)}</td>
                  </tr>

                  {/* Sección: Buen Buró */}
                  <tr className="bg-indigo-100">
                    <td colSpan={2} className="px-3 py-2 font-bold text-indigo-800 border border-gray-300">10. Buen Buró</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Estatus Buen Buró</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.buenBuroEstatus || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Oferta</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.buenBuroMontoOferta)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Aceptado</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.buenBuroMontoAceptado)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Buen Buró</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.montoBuenBuro)}</td>
                  </tr>

                  {/* Sección: Historial SIAC */}
                  <tr className="bg-gray-200">
                    <td colSpan={2} className="px-3 py-2 font-bold text-gray-800 border border-gray-300">11. Historial SIAC (Crédito Anterior)</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Núm. Control Anterior</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.solicitudNumeroControlAnterior || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Monto Original Anterior</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.solicitudMontoOriginalAnterior)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Saldo Actual SIAC</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.solicitudSaldoActualSIACAnterior)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Total Pagado SIAC</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.solicitudTotalPagadoSIACAnterior)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Avance Crédito Anterior</td>
                    <td className="px-3 py-2 border border-gray-200">{formatPercent(localSolicitud.solicitudAvanceCreditoAnterior)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Saldo Parcialidades</td>
                    <td className="px-3 py-2 border border-gray-200">{formatCurrency(localSolicitud.solicitudSaldoParcialidadesMontoAnterior)} ({localSolicitud.solicitudSaldoParcialidadesNumeroAnterior || 0} pagos)</td>
                  </tr>

                  {/* Sección: Estado del Negocio */}
                  <tr className="bg-cyan-100">
                    <td colSpan={2} className="px-3 py-2 font-bold text-cyan-800 border border-gray-300">12. Estado del Negocio</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Estatus Solicitud</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.solicitudEstatus || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Etapa Embudo</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.etapaEmbudo || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Tipo de Negocio</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.tipoDeNegocio || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Autorización DG</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.autorizacionDG || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Motivo No Cierre</td>
                    <td className="px-3 py-2 border border-gray-200 text-red-600">{localSolicitud.motivoNoCierre || '—'}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">VoBo Requerido</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.voboRequerido || '—'}</td>
                  </tr>

                  {/* Sección: Observaciones y Comentarios */}
                  {(localSolicitud.observacionesAnalisis || localSolicitud.comentarios) && (
                  <>
                  <tr className="bg-gray-100">
                    <td colSpan={2} className="px-3 py-2 font-bold text-gray-800 border border-gray-300">Observaciones y Comentarios</td>
                  </tr>
                  {localSolicitud.observacionesAnalisis && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Observaciones Análisis</td>
                    <td className="px-3 py-2 border border-gray-200 whitespace-pre-wrap">{localSolicitud.observacionesAnalisis}</td>
                  </tr>
                  )}
                  {localSolicitud.comentarios && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Comentarios</td>
                    <td className="px-3 py-2 border border-gray-200 whitespace-pre-wrap">{localSolicitud.comentarios}</td>
                  </tr>
                  )}
                  </>
                  )}

                  {/* Sección: Excepciones Documentadas */}
                  {(localSolicitud.excepcionDocumentada1 || localSolicitud.excepcionDocumentada2 || localSolicitud.excepcionDocumentada3) && (
                  <>
                  <tr className="bg-amber-100">
                    <td colSpan={2} className="px-3 py-2 font-bold text-amber-800 border border-gray-300">Excepciones Documentadas</td>
                  </tr>
                  {localSolicitud.excepcionDocumentada1 && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Excepción 1</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.excepcionDocumentada1}</td>
                  </tr>
                  )}
                  {localSolicitud.excepcionDocumentada2 && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Excepción 2</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.excepcionDocumentada2}</td>
                  </tr>
                  )}
                  {localSolicitud.excepcionDocumentada3 && (
                  <tr>
                    <td className="px-3 py-2 text-right font-medium text-gray-600 border border-gray-200">Excepción 3</td>
                    <td className="px-3 py-2 border border-gray-200">{localSolicitud.excepcionDocumentada3}</td>
                  </tr>
                  )}
                  </>
                  )}

                  {/* Sección: Propuestas */}
                  {(isPropuestaValida(localSolicitud.propuestaSolicitud) || isPropuestaValida(localSolicitud.propuestaRecomendacion) || isPropuestaValida(localSolicitud.propuestaBuenBuro)) && (
                  <>
                  <tr className="bg-violet-100">
                    <td colSpan={2} className="px-3 py-2 font-bold text-violet-800 border border-gray-300">Propuestas Calculadas</td>
                  </tr>
                  {isPropuestaValida(localSolicitud.propuestaSolicitud) && (
                  <tr>
                    <td colSpan={2} className="px-3 py-2 border border-gray-200">
                      <div className="bg-violet-50 rounded-lg p-3">
                        <div className="text-sm font-semibold text-violet-700 mb-2">Propuesta Solicitud</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                          <div><span className="text-gray-500">Monto:</span> <span className="font-semibold text-green-700">{formatCurrency((localSolicitud.propuestaSolicitud as any).montoAutorizado)}</span></div>
                          <div><span className="text-gray-500">Plazo:</span> <span className="font-semibold">{(localSolicitud.propuestaSolicitud as any).plazoAutorizado} meses</span></div>
                          <div><span className="text-gray-500">Tasa:</span> <span className="font-semibold">{formatPercent((localSolicitud.propuestaSolicitud as any).tasaAutorizada)}</span></div>
                          <div><span className="text-gray-500">Comisión:</span> <span className="font-semibold">{formatPercent((localSolicitud.propuestaSolicitud as any).tasaDeComision)}</span></div>
                          <div><span className="text-gray-500">Frecuencia:</span> <span className="font-semibold">{(localSolicitud.propuestaSolicitud as any).frecuenciaDePago}</span></div>
                          <div><span className="text-gray-500">Núm. Pagos:</span> <span className="font-semibold">{(localSolicitud.propuestaSolicitud as any).numeroPagos}</span></div>
                          <div><span className="text-gray-500">Pago/Periodo:</span> <span className="font-semibold">{formatCurrency((localSolicitud.propuestaSolicitud as any).pagoEstimadoPeriodo)}</span></div>
                          <div><span className="text-gray-500">Ministración:</span> <span className="font-semibold">{formatCurrency((localSolicitud.propuestaSolicitud as any).montoMinistracion)}</span></div>
                          <div><span className="text-gray-500">Intereses:</span> <span className="font-semibold">{formatCurrency((localSolicitud.propuestaSolicitud as any).intereses)}</span></div>
                          <div><span className="text-gray-500">Seguro Fin.:</span> <span className="font-semibold">{formatCurrency((localSolicitud.propuestaSolicitud as any).seguroFinanciado)}</span></div>
                          <div><span className="text-gray-500">Primer Cobro:</span> <span className="font-semibold">{formatDate((localSolicitud.propuestaSolicitud as any).fechaPrimerCobro)}</span></div>
                          <div><span className="text-gray-500">Último Pago:</span> <span className="font-semibold">{formatDate((localSolicitud.propuestaSolicitud as any).fechaUltimoPago)}</span></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  )}
                  {isPropuestaValida(localSolicitud.propuestaRecomendacion) && (
                  <tr>
                    <td colSpan={2} className="px-3 py-2 border border-gray-200">
                      <div className="bg-blue-50 rounded-lg p-3">
                        <div className="text-sm font-semibold text-blue-700 mb-2">Propuesta Recomendación</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                          <div><span className="text-gray-500">Monto:</span> <span className="font-semibold text-green-700">{formatCurrency((localSolicitud.propuestaRecomendacion as any).montoAutorizado)}</span></div>
                          <div><span className="text-gray-500">Plazo:</span> <span className="font-semibold">{(localSolicitud.propuestaRecomendacion as any).plazoAutorizado} meses</span></div>
                          <div><span className="text-gray-500">Tasa:</span> <span className="font-semibold">{formatPercent((localSolicitud.propuestaRecomendacion as any).tasaAutorizada)}</span></div>
                          <div><span className="text-gray-500">Comisión:</span> <span className="font-semibold">{formatPercent((localSolicitud.propuestaRecomendacion as any).tasaDeComision)}</span></div>
                          <div><span className="text-gray-500">Frecuencia:</span> <span className="font-semibold">{(localSolicitud.propuestaRecomendacion as any).frecuenciaDePago}</span></div>
                          <div><span className="text-gray-500">Núm. Pagos:</span> <span className="font-semibold">{(localSolicitud.propuestaRecomendacion as any).numeroPagos}</span></div>
                          <div><span className="text-gray-500">Pago/Periodo:</span> <span className="font-semibold">{formatCurrency((localSolicitud.propuestaRecomendacion as any).pagoEstimadoPeriodo)}</span></div>
                          <div><span className="text-gray-500">Ministración:</span> <span className="font-semibold">{formatCurrency((localSolicitud.propuestaRecomendacion as any).montoMinistracion)}</span></div>
                          <div><span className="text-gray-500">Intereses:</span> <span className="font-semibold">{formatCurrency((localSolicitud.propuestaRecomendacion as any).intereses)}</span></div>
                          <div><span className="text-gray-500">Seguro Fin.:</span> <span className="font-semibold">{formatCurrency((localSolicitud.propuestaRecomendacion as any).seguroFinanciado)}</span></div>
                          <div><span className="text-gray-500">Primer Cobro:</span> <span className="font-semibold">{formatDate((localSolicitud.propuestaRecomendacion as any).fechaPrimerCobro)}</span></div>
                          <div><span className="text-gray-500">Último Pago:</span> <span className="font-semibold">{formatDate((localSolicitud.propuestaRecomendacion as any).fechaUltimoPago)}</span></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  )}
                  {isPropuestaValida(localSolicitud.propuestaBuenBuro) && (
                  <tr>
                    <td colSpan={2} className="px-3 py-2 border border-gray-200">
                      <div className="bg-amber-50 rounded-lg p-3">
                        <div className="text-sm font-semibold text-amber-700 mb-2">Propuesta Buen Buró</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                          <div><span className="text-gray-500">Monto:</span> <span className="font-semibold text-green-700">{formatCurrency((localSolicitud.propuestaBuenBuro as any).montoAutorizado)}</span></div>
                          <div><span className="text-gray-500">Plazo:</span> <span className="font-semibold">{(localSolicitud.propuestaBuenBuro as any).plazoAutorizado} meses</span></div>
                          <div><span className="text-gray-500">Tasa:</span> <span className="font-semibold">{formatPercent((localSolicitud.propuestaBuenBuro as any).tasaAutorizada)}</span></div>
                          <div><span className="text-gray-500">Comisión:</span> <span className="font-semibold">{formatPercent((localSolicitud.propuestaBuenBuro as any).tasaDeComision)}</span></div>
                          <div><span className="text-gray-500">Frecuencia:</span> <span className="font-semibold">{(localSolicitud.propuestaBuenBuro as any).frecuenciaDePago}</span></div>
                          <div><span className="text-gray-500">Núm. Pagos:</span> <span className="font-semibold">{(localSolicitud.propuestaBuenBuro as any).numeroPagos}</span></div>
                          <div><span className="text-gray-500">Pago/Periodo:</span> <span className="font-semibold">{formatCurrency((localSolicitud.propuestaBuenBuro as any).pagoEstimadoPeriodo)}</span></div>
                          <div><span className="text-gray-500">Ministración:</span> <span className="font-semibold">{formatCurrency((localSolicitud.propuestaBuenBuro as any).montoMinistracion)}</span></div>
                          <div><span className="text-gray-500">Intereses:</span> <span className="font-semibold">{formatCurrency((localSolicitud.propuestaBuenBuro as any).intereses)}</span></div>
                          <div><span className="text-gray-500">Seguro Fin.:</span> <span className="font-semibold">{formatCurrency((localSolicitud.propuestaBuenBuro as any).seguroFinanciado)}</span></div>
                          <div><span className="text-gray-500">Primer Cobro:</span> <span className="font-semibold">{formatDate((localSolicitud.propuestaBuenBuro as any).fechaPrimerCobro)}</span></div>
                          <div><span className="text-gray-500">Último Pago:</span> <span className="font-semibold">{formatDate((localSolicitud.propuestaBuenBuro as any).fechaUltimoPago)}</span></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  )}
                  </>
                  )}
                </tbody>
              </table>
              )}
            </div>
          )}

          {/* TAB 2: SIMULADOR SIAC */}
          {activeTab === 'simulador-siac' && onSimularSIAC && (
            <SimuladorSIAC
              solicitud={localSolicitud}
              onSimular={onSimularSIAC}
              isSimulating={isSimulating ?? false}
            />
          )}
        </div>
    </>
  );
}
